import { getFirestore, collection, doc, addDoc, getDocs, query, where, orderBy, limit, deleteDoc } from 'firebase/firestore'
import { FitbitUserAPI } from '@/helpers/fitbit'
import { UserServiceAPI } from '@/helpers/service'
// import Target from "@/models/target"
import { isSameDate, dateToLocalMidnight } from '@/helpers/time'
import moment from 'moment'

export default class Sleep {
  constructor(
    id,
    uuid,
    date,
    timeStart,
    timeEnd,
    main,
    duration,
    breakdown,
    ) {
    this.id = id 
    this.uuid = uuid
    this.date = dateToLocalMidnight(date) //timestamp at 00:00:00 UTC+10/11 (user's local time)
    this.timeStart = new Date(timeStart)
    this.timeEnd = new Date(timeEnd)
    this.main = main //boolean
    this.duration = duration //number
    this.breakdown = breakdown //map
    this.apiServiceUser = new UserServiceAPI()
  }

  static firestoreConverter() {
    return {
      toFirestore: (sleep) => {
        return {
          id: sleep.id,
          uuid: doc(getFirestore(), 'users', sleep.uuid),
          date: sleep.date,
          timeStart: sleep.timeStart,
          timeEnd: sleep.timeEnd,
          main: sleep.main,
          duration: sleep.duration,
          breakdown: sleep.breakdown,
        }
      },
      fromFirestore: (snapshot, options) => {
        const data = snapshot.data(options)
        data.breakdown.data.map((period) => period.time = period.time.toDate())

        return new Sleep(
          snapshot.id,
          data.uuid.path.split('/')[1],
          data.date.toDate(),
          data.timeStart.toDate(),
          data.timeEnd.toDate(),
          data.main,
          data.duration,
          data.breakdown,
        )
      }
    }
  }

  get ref() {
    if (!this._ref) {
      this._ref = doc(getFirestore(), 'sleeps', this.id).withConverter(Sleep.firestoreConverter())
    }
    return this._ref
  }

  static collection() {
    return collection(getFirestore(), 'sleeps').withConverter(Sleep.firestoreConverter())
  }

  get opportunity() {
    const start = moment(this.timeStart)
    const end = moment(this.timeEnd)
    return moment.duration(end.diff(start)).as('minutes')
  }

  get efficiency() {
    return Math.round(this.duration / this.opportunity * 1e2) / 1e2
  }

  get latency() {
    // Get the first sleep stage and return as latency (minutes) if is it a 'wake stage'.
    for (const period of this.breakdown.data) {
      if (this.timeStart.getTime() === period.time.getTime() && period.stage === 'wake') {
        return period.seconds / 60
      }
    }
    // If the first sleep stage recorded is not 'wake', then latency is 0.
    return 0
  }

  get consistency() {
    const result = this.apiServiceUser.getSleepConsistency(this.date.format(), this.date.format())
      .then(res => {
        return res?.score
      })
    return result
  }

  // TODO: This will be a complex calculation that takes into account
  //         1. The different proportion of the sleep stages.
  //         2. ?
  // For now just return efficiency.
  get quality() { return this.efficiency }

  static async getUserSleepLatest(userID) {
    const ref = Sleep.collection()
    const userRef = doc(getFirestore(), 'users', userID)
    const q = query(ref, where('uuid', '==', userRef), orderBy('date', 'desc'), limit(1))
    const snap = await getDocs(q)
    let result = null
    snap.forEach((doc) => {
      result = doc.data()
    })
    return result
  }

  static async getSleeps(userID, dateFrom, dateTo, mainOnly = false) {
    const ref = Sleep.collection()
    const userRef = doc(getFirestore(), 'users', userID)
    const q = query(
      ref,
      where('uuid', '==', userRef),
      where('main', 'in', mainOnly ? [true] : [true, false]),
      where('date', '>=', dateToLocalMidnight(dateFrom)),
      where('date', '<=', dateToLocalMidnight(dateTo)),
      orderBy('date', 'asc'),
    )
    const snap = await getDocs(q)
    let result = []
    snap.forEach((doc) => {
      result.push(doc.data())
    })
    return result
  }

  // Admin only.
  static async getSleepsAll(userID) {
    const ref = Sleep.collection()
    const userRef = doc(getFirestore(), 'users', userID)
    const q = query(
      ref,
      where('uuid', '==', userRef),
    )
    const snap = await getDocs(q)
    let result = []
    snap.forEach((doc) => {
      result.push(doc.data())
    })
    return result
  }

  static async syncFitbit(userID, dateFrom) {
    // Get latest sleep date from database.
    const latest = await Sleep.getUserSleepLatest(userID)

    // If there is no sleeps available, sync the last 2 weeks before sign up
    // until today. Otherwise, query Fitbit for sleep log list from that date
    // until today.
    if (latest) {
      let dateCalc = new Date(latest.date)
      let dateToday = new Date()
      if (isSameDate(dateCalc, dateToday)) {
        console.log('Data is up to date, latest entry is today')
        return
      } else {
        dateCalc.setDate(dateCalc.getDate() + 1)
        dateFrom = dateCalc.format()
        console.log('We have sleep data until', dateFrom)
        console.log('Attempting to get latest data from then until today')
      }
    }

    const ref = Sleep.collection()
    const api = new FitbitUserAPI()
    const data = await api.getSleepLogList(dateFrom)
    console.log('New entries:', data)

    for (const s of data) {
      const mapped = await Sleep.mapFitbitSleepLog(userID, s)
      if (!mapped.uuid) {
        throw new Error('A uuid is required to save a sleep.')
      }
      await addDoc(ref, mapped)
    }
  }

  static async mapFitbitSleepLog(userID, log) {
    // Handle minuteData for API Version 1.1:
    let data = log.minuteData.map(m => {
      const timeString = m.dateTime
      m.dateTime = new Date(log.dateOfSleep)
      m.dateTime.setHours(parseInt(timeString.split(':')[0]))
      m.dateTime.setMinutes(parseInt(timeString.split(':')[1]))
      m.dateTime.setSeconds(parseInt(timeString.split(':')[2]))
      return m
    })
    const lastDate = data[data.length - 1].dateTime
    data = data.map(m => {
      if (m.dateTime > lastDate) {
        m.dateTime.setDate(m.dateTime.getDate() - 1)
      }
      return m
    })

    const breakdown = {
      // Fitbit also supports 'asleep', 'awake', 'restless' for naps, old data,
      // and unsupported devices.
      summary: {
        // API Version 1.1:
        minutesAwake: log.minutesAwake || 0,
        minutesCore: log.minutesAsleep || 0,
        minutesDeep: 0,
        minutesREM: 0,
        // API Version 1.2:
        // minutesAwake: log.levels.summary.wake ? log.levels.summary.wake.minutes : 0,
        // minutesCore: log.levels.summary.light ? log.levels.summary.light.minutes : 0,
        // minutesDeep: log.levels.summary.deep ? log.levels.summary.deep.minutes : 0,
        // minutesREM: log.levels.summary.rem ? log.levels.summary.rem.minutes : 0,
      },
      data: Sleep.mapFitbitSleepLogLevelsData(data),
      // data: Sleep.mapFitbitSleepLogLevelsData(log.levels.data),
    }
    return new Sleep(
      null,
      userID,
      log.dateOfSleep,
      log.startTime,
      log.endTime,
      true, // log.isMainSleep,
      log.minutesAsleep,
      breakdown,
    )
  }

  static mapFitbitSleepLogLevelsData(data) {
    let result = []
    for (const level of data) {
      result.push({
        time: level.dateTime, // new Date(level.dateTime),
        stage: level.value == 1 ? 'asleep' : (level.value == 2 ? 'awake' : (level.value == 3 ? 'restless' : null)), // level.level,
        seconds: 60, // level.seconds,
      })
    }
    return result
  }

  async delete() {
    await deleteDoc(this.ref)
  }
}